import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { GET_SUPPORT_USERS, SWITCH_SUPPORT_USER } from '../queries.ts';
import { useMutation, useQuery } from '@apollo/client';
import { DocumentType } from '../../document/constants.ts';
import { withIcon } from '../../core/components/Icon.tsx';
import {
    faCheck,
    faChevronDown,
    faChevronUp,
    faCube,
    faSpinnerThird,
    faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import classnames from '../../core/utils/classnames.tsx';
import { capitalize } from 'lodash';
import SupportUserSelect from './SupportUserSelect.tsx';
import { useLocalStorage } from '../../core/utils/hooks/useLocalStorage.ts';
import { Trans, useTranslation } from 'react-i18next';
import { canUseSupportMode } from '../../users/utils.ts';
import { useApplicationContext } from '../../core/contexts/ApplicationContext.tsx';
import ConfirmModal from '../../core/components/ConfirmModal.tsx';
import SupportCustomerInfo from './SupportCustomerInfo.tsx';

const CubeIcon = withIcon(faCube);
const ChevronDownIcon = withIcon(faChevronDown);
const ChevronUpIcon = withIcon(faChevronUp);
const LoadingIcon = withIcon(faSpinnerThird);
const SuccessIcon = withIcon(faCheck);
const ErrorIcon = withIcon(faXmark);

const SupportBar = ({ user, controls }: any) => {
    const [collapsed, setCollapsed] = useLocalStorage('SUPPORT_BAR_COLLAPSED', true);
    let { supportUsers, switchSupportUser, isSessionUserOutOfSync } = useSupportMode(user);
    const isCollapsed = collapsed && !isSessionUserOutOfSync;

    return (
        <div
            className={classnames(
                'bg-surface-accent-strong-default w-full flex-none relative flex z-[100] justify-center items-center h-full min-h-0 transition-[max-height]',
                isCollapsed ? 'max-h-1' : 'max-h-12',
                // stacked on phones, with room at the bottom for the absolutely positioned environment label
                !isCollapsed &&
                    'max-md:h-auto max-md:max-h-none max-md:flex-col max-md:items-stretch max-md:gap-2 max-md:px-3 max-md:pt-2 max-md:pb-11',
                isSessionUserOutOfSync && 'bg-surface-accent-strong-active'
            )}
        >
            <div
                className={classnames(
                    'flex-1 min-w-0 flex flex-wrap justify-start items-center gap-2 md:flex-nowrap md:px-5 md:gap-4 transition-opacity',
                    isCollapsed ? 'height-0 opacity-0 overflow-hidden pointer-events-none' : 'delay-100 opacity-100'
                )}
            >
                <SupportUserSelect
                    value={user?.email}
                    options={supportUsers?.map((supportUser) => ({
                        label: supportUser.email,
                        value: supportUser.email,
                    }))}
                    onValueChange={(supportUserId) => switchSupportUser({ supportUserId })}
                />

                <SupportCustomerInfo customer={user.customer} isSessionUserOutOfSync={isSessionUserOutOfSync} />
            </div>

            <button
                className={classnames(
                    'shrink-0 whitespace-nowrap rounded px-3 py-1 text-content-white dark:text-content-black gap-2 flex items-center text-sm font-medium hover:bg-surface-accent-strong-hover transition-colors disabled:opacity-50',
                    !collapsed && 'max-md:absolute max-md:left-1/2 max-md:bottom-2 max-md:-translate-x-1/2',
                    collapsed &&
                        'absolute left-1/2 top-full transform -translate-x-1/2 -translate-y-1 bg-surface-accent-strong-default z-10'
                )}
                onClick={() => setCollapsed(!collapsed)}
                disabled={isSessionUserOutOfSync}
            >
                <CubeIcon className="text-xs" /> {capitalize(process.env.RUN_ENV)}{' '}
                {collapsed ? <ChevronUpIcon className="text-xs" /> : <ChevronDownIcon className="text-xs" />}
            </button>

            <div
                className={classnames(
                    'flex-1 min-w-0 flex flex-wrap justify-start items-center gap-2 md:flex-nowrap md:justify-end md:px-5 md:gap-4 transition-opacity',
                    isCollapsed ? 'height-0 opacity-0 overflow-hidden pointer-events-none' : 'delay-100 opacity-100',
                    isSessionUserOutOfSync && 'invisible'
                )}
            >
                {controls}
            </div>
        </div>
    );
};

export const SupportBarButton = ({
    className,
    onClick,
    children,
    ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) => {
    const [state, setState] = useState<string | null>(null);
    const [width, setWidth] = useState(null);

    const ref = useRef(null);

    const handleClick = (e) => {
        setState('loading');
        setWidth(ref.current.offsetWidth);
        Promise.resolve(onClick?.(e))
            .finally(() =>
                setTimeout(() => {
                    setState(null);
                    setWidth(null);
                }, 1000)
            )
            .then(() => setState('success'))
            .catch(() => setState('error'));
    };
    return (
        <button
            ref={ref}
            className={classnames(
                'rounded px-3 py-1 text-content-white dark:text-content-black gap-3 flex items-center whitespace-nowrap text-sm font-medium hover:bg-surface-accent-strong-hover transition-colors disabled:opacity-50 text-center justify-center',
                state !== null && 'bg-surface-accent-default',
                className
            )}
            {...props}
            style={width ? { width } : null}
            onClick={handleClick}
        >
            {state === 'loading' && <LoadingIcon className="text-xs animate-spin" />}
            {state === 'success' && <SuccessIcon className="text-xs" />}
            {state === 'error' && <ErrorIcon className="text-xs" />}
            {state === null && children}
        </button>
    );
};

export const SwitchSupportModal = ({
    canSwitch = false,
    recordId,
    documentType,
    channelId,
}: {
    canSwitch?: boolean;
    recordId?: string;
    documentType?: DocumentType;
    channelId?: string;
    // check if data is null to open or close modal
}) => {
    const { t } = useTranslation('support');

    const { user } = useApplicationContext();
    const { canUseSupportMode, switchSupportUser, isSessionUserOutOfSync } = useSupportMode(user);

    const [isOpen, setIsOpen] = useState(false);

    const hasObjectId = Boolean(recordId || channelId);

    const handleSubmit = async () => {
        if (hasObjectId && documentType != null) {
            await switchSupportUser({ recordId, documentType, channelId });
        }
        setIsOpen(false);
    };

    useEffect(() => {
        if (!canUseSupportMode) return;

        const handleTabActivation = () => {
            // this is only executed when the tab is focused e.g. window is active and tab is active
            // which prevents issues when to many support users are switched simultaneously in the background
            if (!window.document.hidden && canSwitch) {
                setIsOpen(true);
            }
        };

        // Run on initial load if tab is visible
        void handleTabActivation();

        window.document.addEventListener('visibilitychange', handleTabActivation);
        window.addEventListener('focus', handleTabActivation);
        return () => {
            window.document.removeEventListener('visibilitychange', handleTabActivation);
            window.removeEventListener('focus', handleTabActivation);
        };
    }, [canUseSupportMode, recordId, canSwitch]);

    if (!canUseSupportMode || !isOpen) return null;

    return (
        <ConfirmModal
            visible={isOpen}
            onCancel={() => setIsOpen(false)}
            onConfirm={handleSubmit}
            title={isSessionUserOutOfSync ? t('supportModal.outOfSyncTitle') : t('supportModal.differentCustomerTitle')}
            labelConfirm={t('supportModal.submitButton')}
            labelCancel={t('supportModal.cancelButton', { currentUser: user.email })}
        >
            <p>
                {isSessionUserOutOfSync ? (
                    <Trans
                        t={t}
                        i18nKey={'supportModal.outOfSyncDescription'}
                        values={{
                            currentUser: user.email,
                        }}
                        components={{
                            strong: <strong className="font-medium" />,
                        }}
                    />
                ) : (
                    <Trans
                        t={t}
                        i18nKey={'supportModal.differentCustomerDescription'}
                        values={{
                            currentUser: user.email,
                        }}
                        components={{
                            strong: <strong className="font-medium" />,
                        }}
                    />
                )}
            </p>

            <p className="mt-4 text-content-medium">
                <Trans
                    t={t}
                    i18nKey={'supportModal.cancelNote'}
                    values={{
                        currentUser: user.email,
                    }}
                    components={{
                        strong: <strong className="font-medium" />,
                    }}
                />
            </p>
        </ConfirmModal>
    );
};

export const useSupportMode = (user) => {
    if (!canUseSupportMode(user)) {
        return { canUseSupportMode: false, supportUsers: null, switchSupportUser: null, isSessionUserOutOfSync: null };
    }

    const { data } = useQuery(GET_SUPPORT_USERS);
    let supportUsers = data?.supportUsers || [];
    if (!supportUsers.find((u) => u.id === user.id)) {
        supportUsers = [user, ...supportUsers];
    }

    let [isSessionUserOutOfSync, setIsSessionUserOutOfSync] = useState(false);

    const onWindowFocus = () => {
        const refreshedSessionUserId = JSON.parse(sessionStorage.getItem('sessionUserId'));
        const refreshedGlobalUserId = JSON.parse(localStorage.getItem('globalUserId'));
        if (refreshedGlobalUserId != null && refreshedGlobalUserId != refreshedSessionUserId) {
            setIsSessionUserOutOfSync(true);
        } else {
            setIsSessionUserOutOfSync(false);
        }
    };
    useEffect(() => {
        window.addEventListener('focus', onWindowFocus);
        if (!isSessionUserOutOfSync) {
            localStorage.setItem('globalUserId', user.id);
        }
        sessionStorage.setItem('sessionUserId', user.id);
        onWindowFocus();
        return () => {
            window.removeEventListener('focus', onWindowFocus);
        };
    }, [user.id]);

    const [switchSupportUserMutation] = useMutation(SWITCH_SUPPORT_USER);
    const switchSupportUser = ({ supportUserId = null, recordId = null, channelId = null, documentType = null }) => {
        // May provide either support user or record / channel

        const hasObjectId = Boolean(recordId || channelId);

        if (supportUserId != null) {
            supportUserId = parseInt(supportUsers.find((u) => u.email === supportUserId)?.id);
        }
        if (documentType != null) {
            documentType = DocumentType[documentType].toString();
        }

        if (!supportUserId && !hasObjectId) return;
        if (supportUserId != null && supportUserId === parseInt(user.id)) return;

        return switchSupportUserMutation({ variables: { supportUserId, recordId, channelId, documentType } }).then(
            (res) => {
                localStorage.setItem('globalUserId', user.id);
                sessionStorage.setItem('sessionUserId', user.id);

                // ensure we are not stuck on another customers channel or record
                if (recordId == null && channelId == null && documentType == null) {
                    window.location.href = '/';
                } else {
                    window.location.reload();
                }
            }
        );
    };

    return { canUseSupportMode: canUseSupportMode(user), supportUsers, switchSupportUser, isSessionUserOutOfSync };
};

export default SupportBar;
